import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Typography,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@altimateai/lego";
import { getRBACInformation, getUserGroupRulesStats } from "../services/rbac";
import { Loader } from "../components/Loader";
import { OverviewTab } from "./components/OverviewTab";
import { SettingsTab } from "./components/SettingsTab";
import { DatabaseTab } from "./components/DatabaseTab";

export const Rbac = () => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedTab, setSelectedTab] = useState<string>("overview");
  const [selectedRulesTenant, setSelectedRulesTenant] = useState<string>("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["rbac-information"],
    queryFn: getRBACInformation,
  });

  const {
    data: rulesData,
    isLoading: rulesLoading,
    error: rulesError,
  } = useQuery({
    queryKey: ["user-group-rules-stats", selectedRulesTenant],
    queryFn: () => getUserGroupRulesStats(selectedRulesTenant),
  });

  const toggleRow = (rowKey: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  if (isLoading || rulesLoading) {
    return <Loader label="Loading RBAC information..." />;
  }

  if (error || rulesError) {
    return (
      <div className="al-p-6">
        <Card>
          <CardHeader>
            <CardTitle>Error Loading RBAC Information</CardTitle>
          </CardHeader>
          <CardContent>
            <Typography variant="body" className="al-text-red-600">
              {error instanceof Error
                ? error.message
                : rulesError instanceof Error
                  ? rulesError.message
                  : "An error occurred"}
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data || !rulesData) {
    return null;
  }

  const databases = Object.keys(data.policies_by_database);

  return (
    <div className="al-p-6 al-space-y-6">
      {/* Header */}
      <div>
        <Typography variant="h1" weight="semibold">
          Access Control Details
        </Typography>
        <Typography variant="body" className="al-text-gray-600 al-mt-2">
          View ClickHouse policies and User Group access rules
        </Typography>
      </div>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        variant="default"
        onValueChange={(value: string) => {
          setSelectedTab(value);
          if (value === "overview" || databases.includes(value)) {
            setSelectedRulesTenant("all");
          } else if (value !== "settings") {
            setSelectedRulesTenant(value);
          }
        }}
      >
        <TabsList className="al-flex-wrap al-h-auto !al-justify-start">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="settings">Settings & Roles</TabsTrigger>
          {databases.map(db => (
            <TabsTrigger key={db} value={db}>
              {db.toUpperCase()}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab rulesData={rulesData} />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab
            roles={data.roles}
            settings={data.settings}
            roleSettings={data.role_settings}
          />
        </TabsContent>

        {databases.map(database => (
          <TabsContent key={database} value={database}>
            <DatabaseTab
              database={database}
              rulesData={rulesData}
              policiesByDatabase={data.policies_by_database}
              expandedRows={expandedRows}
              toggleRow={toggleRow}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
